import React from 'react';
import { Link } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';
import { AwaitingItem } from '../../../services/monitoringApi';
import { formatLocalDateToBR } from '../../../utils/DateUtils';

/**
 * Célula da coluna "Próx. Mon." (AF-3581).
 *
 * Um documento pode estar aguardando o retorno do Data Provider. Nesse estado a varredura
 * diária deliberadamente NÃO o reenvia, para não abandonar o lote anterior — foi o que
 * causou o incidente de 20/08/2026, em que um lote travou no provedor e o monitoramento
 * ficou esperando um retorno que não viria.
 *
 * Como o desbloqueio é manual (botão Retentar em Relatórios em Lote), a espera precisa
 * estar visível na tela. Sem isso a correção trocaria um bug ruidoso por um silencioso:
 * o documento sairia do ciclo de monitoramento sem ninguém perceber.
 */

const DAYS_UNTIL_STALLED = 3;

/**
 * Segmentos de paginação e ordenação da rota do lote no data-provider.
 *
 * O BatchReportPageView define os mesmos valores como default na desestruturação do
 * useParams, então o link funcionaria sem eles. Mas todos os outros pontos que apontam para
 * essa tela (BatchReportPageList, BatchReportPageForm e o breadcrumb do ReportPageView)
 * informam os segmentos explicitamente. Seguir o padrão evita que este link seja o único a
 * depender de um default declarado em outro componente.
 */
const BATCH_REPORT_ROUTE_DEFAULTS = '0/100/document/ASC';

interface NextMonitoringInfo {
    text: string;
    badge: string;
    color: string;
}

interface NextMonitoringCellProps {
    info: NextMonitoringInfo;
    awaiting?: AwaitingItem;
}

const buildAwaitingLabel = (awaiting: AwaitingItem) => {
    // AF-3447/AF-3581: formatLocalDateToBR trata YYYY-MM-DD como data local. Hoje o
    // awaiting_since vem como ISO completo com offset (executed_at é TIMESTAMPTZ), caso em
    // que new Date() já bastaria, mas o helper compartilhado evita o off-by-one em BRT se a
    // origem passar a ser uma data sem hora.
    const since = formatLocalDateToBR(awaiting.awaiting_since);
    const days = awaiting.days_awaiting;
    const dayLabel = days === 1 ? '1 dia' : `${days} dias`;
    return since ? `Aguardando provedor desde ${since} (${dayLabel})` : `Aguardando provedor (${dayLabel})`;
};

const AwaitingProviderBadge: React.FC<{ awaiting: AwaitingItem }> = ({ awaiting }) => {
    const isStalled = awaiting.days_awaiting >= DAYS_UNTIL_STALLED;
    const label = buildAwaitingLabel(awaiting);
    const tone = isStalled
        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';

    return (
        <div className="flex flex-col gap-1">
            <span
                className={`inline-flex w-fit items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${tone}`}
                title={
                    isStalled
                        ? 'Lote sem retorno do provedor. Use Retentar em Relatórios em Lote para reenviar.'
                        : 'Documento enviado ao provedor, aguardando o resultado.'
                }
            >
                <FiClock aria-hidden="true" />
                {label}
            </span>
            {awaiting.dp_batch_id && (
                <Link
                    to={`/data-provider/batch-report/view/${awaiting.dp_batch_id}/${BATCH_REPORT_ROUTE_DEFAULTS}`}
                    className="w-fit text-xs text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    Ver lote {awaiting.dp_batch_id}
                </Link>
            )}
        </div>
    );
};

const NextMonitoringCell: React.FC<NextMonitoringCellProps> = ({ info, awaiting }) => {
    if (awaiting) return <AwaitingProviderBadge awaiting={awaiting} />;

    return (
        <div className="flex items-center gap-2">
            <span className="text-gray-700 dark:text-gray-300">{info.text}</span>
            {info.badge && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${info.color}`}>
                    {info.badge}
                </span>
            )}
        </div>
    );
};

export default NextMonitoringCell;
